import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Phone, Zap } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES } from "@/lib/categories";
import { saveUser, getParrainage, clearParrainage } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const categorieInitiale = CATEGORIES[params.get("cat")] ? params.get("cat") : "lago";
  const [form, setForm] = useState({ firstname: "", contact: "", category: categorieInitiale });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim()) return toast.error("Entrez votre nom ou prénom.");
    if (form.contact.replace(/\D/g, "").length < 8) return toast.error("Entrez votre numéro de téléphone.");
    setSubmitting(true);
    try {
      const parrainage = getParrainage();
      const { data } = await axios.post(`${API}/profiles`, {
        firstname: form.firstname.trim(),
        contact: form.contact.trim(),
        category: form.category,
        parrain_id: parrainage?.parrain_id,
        parrain_annonce_id: parrainage?.annonce_id,
      });
      if (!data.existant) clearParrainage();
      saveUser({ id: data.id, firstname: data.firstname, category: data.category });
      toast.success(
        data.existant ? `Bon retour, ${data.firstname} !` : `Bienvenue sur FodindinCa, ${data.firstname} !`,
        { description: "Choisissez votre accès pour entrer dans votre zone." }
      );
      navigate(`/abonnement?cat=${form.category}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E5DFD5] rounded-xl px-4 py-3.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#E05638]/50 focus:border-[#E05638] transition-all";

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 sm:px-6 pt-32 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Inscription gratuite</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900 mt-3">
            Votre nom, votre numéro. C'est tout.
          </h1>
          <p className="text-stone-600 mt-4 leading-relaxed">
            Pas de code SMS, pas de mot de passe, pas de formulaire interminable. Vous entrez en 15 secondes.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 bg-[#0A5C36]/10 text-[#0A5C36] border border-[#0A5C36]/20 rounded-full px-4 py-2 text-xs font-semibold">
            <Zap size={13} /> Inscription immédiate — aucune vérification par SMS
          </div>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="register-form">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Nom ou prénom *</label>
              <input
                data-testid="register-form-firstname"
                className={inputCls}
                placeholder="Ex : Awa"
                value={form.firstname}
                onChange={set("firstname")}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Numéro de téléphone *</label>
              <div className="relative">
                <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="register-form-contact"
                  inputMode="tel"
                  className={`${inputCls} pl-11`}
                  placeholder="07 00 00 00 00"
                  value={form.contact}
                  onChange={set("contact")}
                />
              </div>
              <p className="text-xs text-stone-500 mt-2">
                C'est votre identifiant : il servira à vous reconnecter. Jamais affiché sans votre accord.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-3">Votre zone *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.values(CATEGORIES).map((cat) => {
                  const active = form.category === cat.id;
                  return (
                    <button
                      type="button"
                      key={cat.id}
                      data-testid={`register-category-${cat.id}`}
                      onClick={() => setForm({ ...form, category: cat.id })}
                      className={`relative text-left p-4 rounded-2xl border-2 transition-all duration-200 ${
                        active ? "bg-white shadow-md" : "bg-white/60 border-[#E5DFD5] hover:border-stone-300"
                      }`}
                      style={active ? { borderColor: cat.color } : {}}
                    >
                      {active && (
                        <span
                          className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: cat.color }}
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                      )}
                      <div className="font-heading font-bold text-sm text-stone-900">{cat.name}</div>
                      <div className="text-xs text-stone-500 mt-1">{cat.subtitle}</div>
                      <div className="text-[11px] font-semibold mt-2" style={{ color: cat.color }}>{cat.pricing}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="register-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E05638] hover:bg-[#C84428] disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
              Entrer sur FodindinCa
            </button>
            <p className="text-xs text-stone-500 text-center">
              Photo, âge et présentation se complètent plus tard, uniquement si vous publiez une annonce.
              <br />
              Déjà membre ?{" "}
              <Link to="/connexion" className="text-[#E05638] font-semibold" data-testid="register-login-link">
                Se connecter
              </Link>
            </p>
          </form>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
